/* ====================
    IMPORTS & PACKAGES
   ==================== */
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { initializeDatabase } from "../utils/dbInit";
import { auth, db } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import { collection, getDocs, query, where, addDoc } from "firebase/firestore";
import { Lock, Mail, Phone, UserCheck, X, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  /* =============================
      COMPONENT STATES & BINDINGS
     ============================= */

  /* Basic Sign-In Input States */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /* Forgot Password Wizard States */
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailConfirm, setResetEmailConfirm] = useState("");
  const [resetPhone, setResetPhone] = useState("");
  const [resetRole, setResetRole] = useState("student");

  /* Dialog Popups & Alerts States */
  const [customAlert, setCustomAlert] = useState(null); 
  const alertModalRef = useRef(null);

  /* ====================================
      SYSTEM INITIALIZATION & LIFECYCLE
     ==================================== */

  useEffect(() => {
    initializeDatabase().catch(err => console.error("Database Initializer Failure: ", err));
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (alertModalRef.current && !alertModalRef.current.contains(event.target)) {
        setCustomAlert(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ==============================
      LOGIN & AUTH ACTION HANDLERS
     ============================== */

  /* Submit Sign-In Credentials */
  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setError("");
      setLoading(true);
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError("Invalid credentials. Please verify your email and password.");
    }
    setLoading(false);
  }

  /* Submit Password Reset Request */
  async function handleForgotPasswordSubmit(e) {
    e.preventDefault();
    if (resetEmail !== resetEmailConfirm) {
      setCustomAlert({ type: "error", message: "⚠️ Confirmation Error: Email inputs do not match!" });
      return;
    }

    setLoading(true);
    try {
      const q = query(
        collection(db, "users"),
        where("email", "==", resetEmail),
        where("phone", "==", resetPhone),
        where("role", "==", resetRole)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setCustomAlert({ type: "error", message: "⚠️ Verification Failed: No matching user found with these details." });
        setLoading(false);
        return;
      }

      await sendPasswordResetEmail(auth, resetEmail);
      
      await addDoc(collection(db, "auditLogs"), {
        action: "Password Reset Request",
        details: `Dispatched secure password reset link to ${resetEmail} (${resetRole})`,
        timestamp: new Date().toISOString()
      });

      setCustomAlert({ type: "success", message: "Password reset link has been sent! Please check your email inbox or spam folder." });
      setIsForgotPassword(false);
      
      // Clear the form fields
      setResetEmail("");
      setResetEmailConfirm("");
      setResetPhone("");
    } catch (err) {
      setCustomAlert({ type: "error", message: "Reset failed: " + err.message });
    }
    setLoading(false);
  }

  /* ==============================
      SIGN-IN PORTAL VIEW RENDERER
     ============================== */
  return (
    <div 
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "var(--bg-slate)",
        padding: "1.5rem"
      }}
    >
      {/* Custom Alert Popups */}
      {customAlert && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(15,23,42,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999, padding: "1.5rem" }}>
          <div className="card" ref={alertModalRef} style={{ maxWidth: "450px", width: "100%", padding: "2rem", textAlign: "center", position: "relative" }}>
            <button onClick={() => setCustomAlert(null)} style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", color: "var(--danger)", cursor: "pointer" }}><X size={20} /></button>
            <h3 style={{ color: customAlert.type === "success" ? "var(--success)" : "var(--danger)", marginBottom: "1rem" }}>
              {customAlert.type === "success" ? "Success!" : "Notice / Error"}
            </h3>
            <p style={{ color: "var(--text-main)", marginBottom: "1.5rem" }}>{customAlert.message}</p>
            <button className="navy-btn" onClick={() => setCustomAlert(null)} style={{ margin: "0 auto" }}>Close Alert</button>
          </div>
        </div>
      )}

      <div 
        className="card" 
        style={{ 
          maxWidth: "450px", 
          width: "100%", 
          margin: "0", 
          padding: "2.5rem 2rem",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        }}
      >
        {/* Header Brand Layout */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div 
            style={{ 
              display: "inline-flex", 
              background: "#b7cbf7", 
              padding: "1rem", 
              borderRadius: "50%",
              marginBottom: "1rem"
            }}
          >
            <img src="/logo192.png" alt="Logo" style={{ width: "70px", height: "auto" }} />
          </div>
          <h2 style={{ color: "var(--navy-primary)", fontSize: "1.75rem", fontWeight: "800", marginBottom: "0.5rem" }}>
            {isForgotPassword ? "Reset Password" : "Oncode APT Login"}
          </h2>
          <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
            {isForgotPassword ? <>Verify details to request reset link for <br /> Oncode APT System</> : <>Enter your credentials to access the <br /> Oncode APT System.</>}
          </p>
        </div>

        {error && !isForgotPassword && (
          <div 
            style={{ 
              color: "var(--danger)", 
              backgroundColor: "#fee2e2", 
              padding: "0.75rem 1rem", 
              borderRadius: "8px", 
              fontSize: "0.85rem",
              fontWeight: "600",
              marginBottom: "1.5rem",
              textAlign: "center"
            }}
          >
            {error}
          </div>
        )}

        {/* Main Sign-In Form Layout */}
        {!isForgotPassword ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Mail size={16} /> Email Address
              </label>
              <input 
                type="email" 
                className="form-control" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>

            <div className="form-group" style={{ marginBottom: "1rem" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Lock size={16} /> Password
              </label>
              <div style={{ position: "relative" }}>
                <input 
                  type={showPassword ? "text" : "password"} 
                  className="form-control" 
                  required 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  style={{ paddingRight: "2.5rem" }}
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center" }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div style={{ textAlign: "right", marginBottom: "1.5rem" }}>
              <button 
                type="button" 
                onClick={() => setIsForgotPassword(true)}
                style={{ background: "none", border: "none", color: "var(--navy-light)", fontWeight: "600", cursor: "pointer", fontSize: "0.85rem" }}
              >
                Forgot Password?
              </button>
            </div>

            <button 
              type="submit" 
              className="navy-btn" 
              style={{ width: "100%", justifyContent: "center", padding: "1rem" }} 
              disabled={loading}
            >
              Sign In to  Oncode APT System
            </button>
          </form>
        ) : (
          /* Forgot Password Verification Form Layout */
          <form onSubmit={handleForgotPasswordSubmit}>
            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Mail size={16} /> Email Address</label>
              <input 
                type="email" 
                className="form-control" 
                required 
                value={resetEmail} 
                onChange={(e) => setResetEmail(e.target.value)} 
              />
            </div>

            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Mail size={16} /> Re-enter Email Address</label>
              <input 
                type="email" 
                className="form-control" 
                required 
                value={resetEmailConfirm} 
                onChange={(e) => setResetEmailConfirm(e.target.value)} 
              />
            </div>

            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Phone size={16} /> Registered Mobile Number</label>
              <input 
                type="text" 
                className="form-control" 
                required 
                value={resetPhone} 
                onChange={(e) => setResetPhone(e.target.value)} 
              />
            </div>

            <div className="form-group" style={{ marginBottom: "2rem" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><UserCheck size={16} /> Select Role</label>
              <select className="form-control" value={resetRole} onChange={(e) => setResetRole(e.target.value)}>
                <option value="student">Student</option>
                <option value="instructor">Instructor</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: "1rem" }}>
              <button 
                type="submit" 
                className="navy-btn" 
                style={{ flex: 1, justifyContent: "center" }}
                disabled={loading}
              >
                Send Reset Link
              </button>
              <button 
                type="button" 
                className="navy-btn navy-btn-danger" 
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => setIsForgotPassword(false)}
              >
                Back to Login
              </button>
            </div>
          </form>
        )}

        <p style={{ textAlign: "center", marginTop: "1.5rem", color: "var(--text-muted)", fontSize: "0.9" }}>
          New student or instructor? <Link to="/register" style={{ color: "var(--navy-light)", fontWeight: "600", textDecoration: "none" }}>Register</Link>
        </p>
      </div>
    </div>
  );
}